import React, { useState, useRef } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { INACTIVE_DESTINATIONS } from "@/lib/destinations";
import { getAttribution } from "@/lib/attribution";
import { CommsConsent } from "@/components/CommsConsent";
import { buildCommercialConsent } from "@/lib/commercialConsent";
import { metaLead } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OPTIONS = [...INACTIVE_DESTINATIONS, "Other"];

// Tertiary conversion: future destinations waitlist. Never styled like the primary/secondary CTAs.
export function WaitlistForm({ copy = {} }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [destination, setDestination] = useState("");
  const [other, setOther] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const leadFired = useRef(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!firstName.trim()) return setError("Please tell us your first name.");
    if (!EMAIL_RE.test(email.trim())) return setError("Please enter a valid email address.");
    if (!destination) return setError("Please choose a destination.");
    setState("loading");
    trackEvent("waitlist_submit", { destination });
    try {
      await axios.post(`${API}/waitlist`, { first_name: firstName.trim(), email: email.trim(), destination, destination_other: other.trim() || null, phone: phone.trim() || null, attribution: getAttribution(), commercial_consent: buildCommercialConsent({ sms: smsOptIn }) });
      if (!leadFired.current) { leadFired.current = true; metaLead("waitlist"); }
      setState("success");
      trackEvent("waitlist_success", { destination });
    } catch (err) {
      setState("error");
      setError("Something went wrong on our side. Please try again in a moment.");
      trackEvent("waitlist_error", { status: err?.response?.status || 0 });
    }
  };

  const label = destination === "Other" && other.trim() ? other.trim() : destination;
  const inputCls = "w-full bg-transparent border-b border-[#451C25]/30 focus:border-[#451C25] text-[#451C25] placeholder:text-[#451C25]/40 py-2.5 focus:outline-none text-[15px]";
  const labelCls = "block text-[10px] tracking-[0.24em] uppercase text-[#897756] mb-1";

  if (state === "success") {
    return (
      <div data-testid="waitlist-success" className="flex items-start gap-3 text-[#451C25]">
        <CheckCircle2 size={20} className="text-[#897756] shrink-0 mt-0.5" />
        <div>
          <p className="text-lg" style={{ fontFamily: "Kanit, Arial, sans-serif", fontWeight: 500 }}>{copy.success_title || "You're on the list."}</p>
          <p className="mt-1 text-[#6B4954] text-[15px]">{(copy.success_text || "We'll let you know when Destino Europe expands to {destination}.").replace("{destination}", label)}</p>
        </div>
      </div>
    );
  }

  return (
    <form data-testid="waitlist-form" onSubmit={onSubmit} noValidate className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <label className="block">
        <span className={labelCls}>First name</span>
        <input data-testid="waitlist-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputCls} placeholder="Linda" autoComplete="given-name" />
      </label>
      <label className="block">
        <span className={labelCls}>Email</span>
        <input data-testid="waitlist-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="linda@example.com" autoComplete="email" />
      </label>
      <fieldset className="sm:col-span-2">
        <legend className={labelCls}>Destination</legend>
        <div className="flex flex-wrap gap-2.5">
          {OPTIONS.map((o) => (
            <button
              key={o}
              type="button"
              data-testid={`waitlist-destination-${o.toLowerCase()}`}
              aria-pressed={destination === o}
              onClick={() => setDestination(o)}
              className={`px-4 py-2 text-[12px] tracking-[0.12em] uppercase border transition-colors ${destination === o ? "bg-[#451C25] text-white border-[#451C25]" : "border-[#451C25]/30 text-[#451C25] hover:border-[#451C25]"}`}
              style={{ fontFamily: "Kanit, Arial, sans-serif" }}
            >
              {o}
            </button>
          ))}
        </div>
      </fieldset>
      {destination === "Other" && (
        <label className="block sm:col-span-2">
          <span className={labelCls}>Which destination? (optional)</span>
          <input data-testid="waitlist-other" value={other} onChange={(e) => setOther(e.target.value)} className={inputCls} placeholder="e.g. France" />
        </label>
      )}
      {error && <p data-testid="waitlist-error" role="alert" className="sm:col-span-2 text-[#8B3A3A] text-[13px]">{error}</p>}
      <label className="block sm:col-span-2">
        <span className={labelCls}>Mobile phone (optional)</span>
        <input data-testid="waitlist-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="+1 305 555 0142" autoComplete="tel" />
      </label>
      <div className="sm:col-span-2">
        <CommsConsent variant="onLight" showSms sms={smsOptIn} onSmsChange={setSmsOptIn} />
      </div>
      <div className="sm:col-span-2">
        <button
          data-testid="waitlist-submit"
          type="submit"
          disabled={state === "loading"}
          className="inline-flex items-center gap-2 border border-[#451C25] text-[#451C25] px-6 py-3 text-[12px] tracking-[0.18em] uppercase hover:bg-[#451C25] hover:text-white transition-colors disabled:opacity-60"
          style={{ fontFamily: "Kanit, Arial, sans-serif" }}
        >
          {state === "loading" ? <><Loader2 size={14} className="animate-spin" /> Joining…</> : <>{copy.cta || "Join the waitlist"} <ArrowRight size={14} /></>}
        </button>
      </div>
    </form>
  );
}

export default WaitlistForm;
